'use client'
import styles from './page.module.css'
import { useGames } from '@/apiUtilities/queries/useGames'
import { useLocale, useTranslations } from 'next-intl'
import GameCard, { CardType } from '@/components/UI/GameCard/GameCard'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ReactNode, useEffect, useState } from 'react'
import SearchFilter from './Filters/Search/SearchFilter'
import FavoritesFilter from './Filters/Favorites/FavoritesFilter'
import IconNoResults from '@/assets/img/Icon_NoResults.svg'
import IconNoResultsFavorites from '@/assets/img/Icon_NoResults_Favorites.svg'
import Button from '@/components/UI/Buttons/Button/Button'
import { useRouter, useSearchParams } from 'next/navigation'
import Filters from './Filters/Filters'
import IconButton, { IconButtonSize } from '@/components/UI/Buttons/IconButton/IconButton'
import IconFilterSettings from '@/assets/img/Icon_FilterSettings.svg'
import { useModalStore } from '@/stores/useModalStore'
import CatalogueFiltersModal from '@/components/Modals/CatalogueFiltersModal/CatalogueFiltersModal'
import { useWindowSize } from '@/hooks/useWindowsSize'
import { BREAKPOINTS } from '@/utils/constants'
import { useUserStore } from '@/stores/useUserStore'
import { pushToDataLayer } from '@/utils/helpers'

export type FilterState = {
	saved: string,
	search: string,
	genres: string,
	supportedControls: string,
	membership: string,
	license: string,
	favorite: string,
	new: string, 
	recommended: string
}

export default function Catalogue() {
	const t = useTranslations('Catalogue')
	const locale = useLocale()
	const params = useSearchParams()
	const { profileId } = useUserStore()
	const [isMounted, setIsMounted] = useState<boolean>(false)
	const [resetKey, setResetKey] = useState(0);
	const { width } = useWindowSize()
	const { openModal, closeModal } = useModalStore()
	const router = useRouter()
	const initialFavorites = params.get('favorite') === 'true'
	const initialSupportedControls = params.get('supported_controls') ?? ''
	const initialMembership = params.get('membership') ?? ''
	const initialLicense = params.get('license') ?? ''
	const initialNew = params.get('new') ?? ''
	const initialRecommended = params.get('recommended') ?? ''
	const initialGenres = params.getAll('genre').map(g => `genre=${encodeURIComponent(g)}`).join('&')
	const [filters, setFilters] = useState<FilterState>({
		saved: '',
		search: '',
		genres: '',
		supportedControls: '',
		membership: '',
		license: '',
		favorite: '',
		new: '',
		recommended: ''
	})
 
	function countFilters() {
		// Cuenta los filtros activos en el estado filters, ignorando 'favorite'
		let total = 0;
		Object.entries(filters).forEach(([key, str]) => {
			if (key === 'favorite') return;
			if (!str) return;
			// Busca ocurrencias de &nombre=valor
			// Coincide con &loquesea=loquesea (nombre puede tener letras, números, guiones bajos)
			const matches = str.match(/[^&=]+=[^&=]+/g);
			if (matches) total += matches.length;
		});
		return total;
	}

	function parseFilters() {
		return Object.values(filters)
			.filter(value => value !== '')
			.map(value => `&${value}`)
			.join('')
	}

	function resetFilters () {
		setFilters({
			saved: '',
			search: '',
			genres: '',
			supportedControls: '',
			membership: '',
			license: '',
			favorite: filters.favorite,
			new: '',
			recommended: ''
		})
		
		setResetKey(prev => prev + 1)
	}

	const currentQueryString = `lang=${locale}&page=0&size=30` + parseFilters()
	const { data, isLoading, isSuccess, isError, fetchNextPage, isFetchingNextPage, hasNextPage } = 
		useGames({ queryString: currentQueryString, enabled: true, profileId: profileId })

	function updateFilters(name: string, value: string) {
		setFilters(prev => {
			return {
				...prev,
				[name]: value
			}
		})
	}

	function filterData() {
		if(!isSuccess) return []
		else {
			// console.log(data)
			return data.pages.flatMap(element => element.games)
		}
	}

	function fetchNext() {
		if(hasNextPage) fetchNextPage()
	}

	const results = filterData()
   
	const CatalogueFiltersMobile = <CatalogueFiltersModal initialFilters={filters} update={updateFilters} resetSignal={resetKey} closeModal={closeModal} filterCount={countFilters()} resetFilters={resetFilters}/>
	function handleOpenModal(modal: ReactNode) {
		openModal({ content: modal })
	}

	/**
	 * Sets the `isMounted` state to true after the component mounts.
	 * This is used to ensure that certain client-only logic (like checking window size) only runs after the component is mounted.
	 * Prevents hydration mismatches by not rendering client-specific UI until after mount.
	 */
	useEffect(() => {
		setIsMounted(true)
	}, [])

	/**
	 * Sync membership from URL into filter state
	 */
	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			membership: initialMembership ? `membership=${initialMembership}` : '',
		}))
	}, [initialMembership])

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			genres: initialGenres && initialGenres !== '' ? '&' + initialGenres : '',
		}))
	}, [initialGenres])


	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			license: initialLicense ? `license=${initialLicense}` : '',
		}))
	}, [initialLicense])

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			new: initialNew ? `new=${initialNew}` : '',
		}))
	}, [initialNew])

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			recommended: initialRecommended ? `recommended=${initialRecommended}` : '',
		}))
	}, [initialRecommended])

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			supportedControls: initialSupportedControls ? `supported_controls=${initialSupportedControls}` : '',
		}))
	}, [initialSupportedControls])

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			favorite: initialFavorites ? `favorite=${initialFavorites}` : '',
		}))
	}, [initialFavorites])

	useEffect(() => {
		router.replace(`?${currentQueryString}`)
	}, [currentQueryString])

	const isDesktop = isMounted && width >= BREAKPOINTS.DESKTOP
  	const isLaptop = isMounted && width >= BREAKPOINTS.LAPTOP
	const [prevSearch, setPrevSearch] = useState('')

	useEffect(() => {
		if(filters.search !== '' && filters.search !== prevSearch) {
			pushToDataLayer({
				event: 'search_submit',
				search_term: filters.search,
				search_location: 'catalogue',
				results_count: data && data.pages ? data?.pages[0].total : 0
			})
			setPrevSearch(filters.search)
		}
	}, [data])
	
	return (
		<div className={styles.main} data-testid="Filters">
			<div className={styles.filters}>
				<div className={styles.controls}>
					<FavoritesFilter update={updateFilters} resetSignal={resetKey} initialValue={initialFavorites}/>
					<div className={styles.searchWrapper} data-testid="Filters_searchWrapper">
						<SearchFilter update={updateFilters} resetSignal={resetKey}/>
						<div className={styles.filtersButtonWrapper}>
							{countFilters() > 0 && <div className={styles.filterCounter}>{countFilters()}</div>}
							<IconButton data-testid="catalogue_openFilters" size={IconButtonSize.L} icon={IconFilterSettings} onClick={() => handleOpenModal(CatalogueFiltersMobile)} className={styles.filtersButton}/>
						</div>
					</div>
				</div>

				{isDesktop && 
				<div className={styles.filtersColumn}>
					<Filters update={updateFilters} resetSignal={resetKey} initialFilters={filters} filterCount={countFilters()} resetFilters={resetFilters} />
				</div>
				}
			</div>

			<div className={styles.results} id='resultsContainer' data-testid="Filters_results">
				{isSuccess && results.length > 0 &&
					<InfiniteScroll
						dataLength={results.length}
						next={fetchNext}
						hasMore={hasNextPage}
						loader=''
						scrollableTarget='resultsContainer'
					>
						<div className={styles.resultsWrapper} data-testid="Filters_resultsWrapper">
							{results.map((game, idx) => 
								<GameCard analyticsModule='catalogue' analyticsIndex={idx} key={game.game_id} game={game} type={isLaptop ? CardType.Horizontal : CardType.Vertical }/>
							)}
						</div>
					</InfiniteScroll>
				}
				{results.length === 0 && filters.favorite === '' &&
					<div className={styles.noResultsWrapper} data-testid="Filters_noResultsWrapper">
						<IconNoResults />
						<p className='bodyTextLarge' data-testid="Filters_noResultsText">{t('noResults')}</p>
						{countFilters() > 0 && <Button data-testid="catalogue_clearFilters" onClick={resetFilters}>{t('clearFilters')}</Button>}
					</div>
				}
				{results.length === 0 && filters.favorite !== '' &&
					<div className={styles.noResultsWrapper} data-testid="Filters_noSavedResultsWrapper">
						<IconNoResultsFavorites />
						<p className='bodyTextLarge' data-testid="Filters_noSavedResultsText">{t('noResultsFavorites')}</p>
					</div>
				}
			</div>
		</div>
	)
}